import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { SparklesIcon, CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/outline'
import type { ProfileType } from '@/types'
import { getRoleJourney, type UnlockAction } from '@/data/roleJourney'
import { profileApi } from '@/api/profile'
import { useAuthStore } from '@/stores/auth.store'

interface FeatureJourneyProps {
  /** Il traguardo che questa funzione rappresenta (es. INSTRUCTOR). */
  target: ProfileType
  /** Titolo della funzione che l'utente stava cercando di raggiungere (opzionale). */
  featureName?: string
  /** Mostra il pulsante "torna indietro" (default true). */
  showBack?: boolean
  /** Variante compatta per card in-linea (default false = pagina piena). */
  compact?: boolean
}

/**
 * Comunica una funzione riservata come PROSSIMO PASSO del percorso di crescita
 * dell'utente, con tono empatico e CTA per avvicinarsi — mai come muro.
 * Coerente col principio di prodotto: empatia, coinvolgimento, supporto, motivazione.
 */
export default function FeatureJourney({ target, featureName, showBack = true, compact = false }: FeatureJourneyProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const journey = getRoleJourney(target)
  const setProfileType = useAuthStore((s) => s.setProfileType)

  // Attivazione self-service del profileType (Subacqueo/Apneista/Fotografo): nessuna
  // approvazione, l'utente entra subito nel suo percorso. Aggiorna backend + store.
  const activateMut = useMutation({
    mutationFn: (type: ProfileType) => profileApi.updateMyProfile({ profileType: type }),
    onSuccess: (_data, type) => {
      setProfileType(type)
      toast.success(t('profileActivation.profileDone'))
      navigate('/app/dashboard')
    },
    onError: () => toast.error(t('profileActivation.profileError')),
  })

  function runAction(action: UnlockAction) {
    switch (action.kind) {
      case 'request-role':
        navigate('/app/profile/request-role-wizard')
        break
      case 'add-certification':
        navigate('/app/profile/cert-wizard')
        break
      case 'complete-profile':
        navigate('/app/profile')
        break
      case 'become-center':
        navigate('/app/divecenter/wizard')
        break
      case 'set-profile-type':
        activateMut.mutate(action.type)
        break
      case 'link':
        navigate(action.to)
        break
    }
  }

  // Fallback empatico se non abbiamo un journey definito per questo target.
  if (!journey) {
    return (
      <div className={compact ? 'rounded-2xl border border-ocean-100 bg-ocean-50/50 p-5' : 'max-w-lg mx-auto text-center py-12 px-4'}>
        <div className="w-14 h-14 rounded-2xl bg-ocean-100 flex items-center justify-center mx-auto mb-4">
          <SparklesIcon className="w-7 h-7 text-ocean-600" />
        </div>
        <h2 className="text-lg font-semibold text-slate-800">{t('auto.componentsUiFeatureJourney.unaNuovaTappaTiAspetta')}</h2>
        <p className="text-sm text-slate-500 mt-2">
          {featureName ? `"${featureName}" fa parte di un percorso dedicato.` : 'Questa funzione fa parte di un percorso dedicato.'} Continua a crescere su eDive per sbloccarla.
        </p>
        {showBack && (
          <button onClick={() => navigate('/app/dashboard')} className="mt-5 text-sm text-ocean-600 hover:text-ocean-700 font-medium">
            Torna alla dashboard
          </button>
        )}
      </div>
    )
  }

  const container = compact
    ? 'rounded-2xl border border-ocean-100 bg-gradient-to-br from-ocean-50 to-teal-50/40 p-5'
    : 'max-w-xl mx-auto py-10 px-4'

  return (
    <div className={container}>
      {/* Hero aspirazionale */}
      <div className={compact ? 'flex items-start gap-3' : 'text-center'}>
        <div className={`${compact ? '' : 'mx-auto mb-4'} w-14 h-14 rounded-2xl bg-white shadow-sm flex items-center justify-center text-3xl shrink-0`}>
          <span aria-hidden>{journey.icon}</span>
        </div>
        <div className={compact ? 'flex-1' : ''}>
          <h2 className={`font-bold text-slate-800 ${compact ? 'text-base' : 'text-xl'}`}>{journey.title}</h2>
          <p className={`text-slate-600 mt-1 ${compact ? 'text-sm' : 'text-base'}`}>{journey.headline}</p>
        </div>
      </div>

      {/* Cosa sblocchi (benefici, in positivo) */}
      <div className={compact ? 'mt-4' : 'mt-6'}>
        <p className="text-xs font-semibold uppercase tracking-wide text-ocean-700 mb-2">{t('auto.componentsUiFeatureJourney.cosaTiAspetta')}</p>
        <ul className="space-y-1.5">
          {journey.unlocks.map((u) => (
            <li key={u} className="flex items-start gap-2 text-sm text-slate-700">
              <SparklesIcon className="w-4 h-4 text-ocean-500 mt-0.5 shrink-0" />
              <span>{u}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* Cosa serve (requisiti, come tappe non come muri) */}
      <div className={compact ? 'mt-4' : 'mt-6'}>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">{t('auto.componentsUiFeatureJourney.perArrivarciTiServe')}</p>
        <ul className="space-y-1.5">
          {journey.requirements.map((r) => (
            <li key={r} className="flex items-start gap-2 text-sm text-slate-600">
              <CheckCircleIcon className="w-4 h-4 text-teal-500 mt-0.5 shrink-0" />
              <span>{r}</span>
            </li>
          ))}
        </ul>
      </div>

      {/* CTA — il prossimo passo */}
      <div className={`flex flex-col sm:flex-row gap-2 ${compact ? 'mt-5' : 'mt-8 justify-center'}`}>
        <button
          onClick={() => runAction(journey.primaryAction.action)}
          className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-xl bg-ocean-600 hover:bg-ocean-700 text-white text-sm font-medium"
        >
          {journey.primaryAction.label}
          <ArrowRightIcon className="w-4 h-4" />
        </button>
        {journey.secondaryAction && (
          <button
            onClick={() => runAction(journey.secondaryAction!.action)}
            className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-medium"
          >
            {journey.secondaryAction.label}
          </button>
        )}
      </div>

      {showBack && !compact && (
        <div className="text-center mt-5">
          <button onClick={() => navigate('/app/dashboard')} className="text-sm text-slate-400 hover:text-slate-600">
            Torna alla dashboard
          </button>
        </div>
      )}
    </div>
  )
}
